import {
	ClearFiltersEvent,
	FilterLinkEvent,
	JumpEvent,
	RemoveFiltersEvent,
	SetFiltersEvent,
	ToggleFiltersEvent
} from "../../events";
import { FilterValue } from "../../lib/filter";
import { BindRecord } from "./frontend";
import { ItemId, Serializer } from "../../model/datasource";
import { AnswerType } from "../../lib/userdata";
import Navigation from "../../lib/navigation";

export class ListFrontend<I extends ItemId> {
	itemIdSerializer: Serializer<I>;
	constructor(item: Serializer<I>) {
		this.itemIdSerializer = item;
	}

	getBindJump(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.item-list .item'),
			'click',
			element => new JumpEvent(element.dataset.id)
		);
	}

	getBindToggleFilter(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.filter-list .filter input:not(.invisible)'),
			'change',
			element => new ToggleFiltersEvent([new FilterValue(element.dataset.filter).id])
		);
	}

	getBindSetFilter(): BindRecord {
		return new BindRecord(
			[window],
			'popstate',
			() => new SetFiltersEvent(Navigation.parseURLFilters())
		);
	}

	getBindFilterLinks(): BindRecord {
		return new BindRecord<HTMLAnchorElement>(
			document.querySelectorAll('.filter-link'),
			'click',
			element => new FilterLinkEvent(element)
		)
	}

	bindCollapseIcons(): void {
		const triggers = document.querySelectorAll<HTMLElement>([
			'a[data-toggle="collapse"]',
			'a[data-bs-toggle="collapse"]',
			'button[data-bs-toggle="collapse"]',
		].join(','));
		for(const trigger of Array.from(triggers)) {
			const collapse = document.querySelector(
				trigger.getAttribute('data-bs-target') ||
				trigger.getAttribute('data-target') ||
				trigger.getAttribute('href')
			);
			const triggerIcons = trigger.querySelectorAll('i');
			for (const icon of Array.from(triggerIcons)) {
				const collapseClass = icon.getAttribute('icon-collapse') || 'fa-minus';
				const expandClass = icon.getAttribute('icon-expand') || 'fa-plus';
				collapse.addEventListener('show.bs.collapse', e => {
					icon.classList.replace(expandClass, collapseClass);
				});
				collapse.addEventListener('hide.bs.collapse', e => {
					icon.classList.replace(collapseClass, expandClass);
				});
			}
		}
	}

	bindSectionHighlight(): void {
		const itemList = Array.from(
			document.querySelectorAll<HTMLElement>('.item-list .item')
		);
		itemList.forEach(item => {
			const same = itemList.filter(i =>
				i.dataset.section_id === item.dataset.section_id
			);
			item.addEventListener('mouseover', e => {
				item.classList.remove('border-light', 'border-dark');
				item.classList.add('border-primary');
				same.forEach(item => {
					item.style.filter = 'opacity(90%)';
					item.classList.add('font-weight-bold');
				});
			});
			item.addEventListener('mouseleave', e => {
				item.classList.remove('border-primary');
				if (item.classList.contains('current')) {
					item.classList.add('border-dark');
				} else {
					item.classList.add('border-light');
				}
				same.forEach(item => {
					item.style.filter = '';
					if (!item.classList.contains('current')) {
						item.classList.remove('font-weight-bold');
					}
				});
			});
		});
	}

	displayFlagged(item: I, flagged: boolean, status: undefined | "loading" | "error"): void {
		const icon = document.querySelector<HTMLElement>(
			`.item-list .item[data-id="${CSS.escape(this.itemIdSerializer.toString(item))}"] i.flag-icon`
		);
		icon.classList.remove('fas', 'fa-flag', 'fa-exclamation-triangle', 'spinner-border', 'spinner-border-sm');
		if (flagged && !status) {
			icon.classList.add('fas', 'fa-flag');
		} else if (status === 'error') {
			icon.classList.add('fas', 'fa-exclamation-triangle');
		} else if (status === 'loading') {
			icon.classList.add('spinner-border', 'spinner-border-sm');
		}
	}

	displaySelectedAnswer(item: I, answer: AnswerType | null, status: undefined | "loading" | "error"): void {
		const current = document.querySelector<HTMLElement>(
			`.item-list .item[data-id="${CSS.escape(this.itemIdSerializer.toString(item))}"]`
		);
		const icon = current.querySelector<HTMLElement>('i.answer-icon');
		icon.classList.remove('fas', 'fa-circle', 'fa-exclamation-triangle', 'spinner-border', 'spinner-border-sm');
		if (answer !== null && answer && !status) {
			icon.classList.add('fas', 'fa-circle');
		} else if (status === 'error') {
			icon.classList.add('fas', 'fa-exclamation-triangle');
		} else if (status === 'loading') {
			icon.classList.add('spinner-border', 'spinner-border-sm');
		}
		if (!status) {
			current.style.backgroundColor = current.style.backgroundColor.replace(
				/rgba?\((?<color>(?:[\d.]+,\s*){2}[\d.]+)(?<alpha>|,\s*[\d.]+)\)/,
				`rgba($<color>, ${answer !== null ? '1.0' : '0.5'})`
			);
		}
	}

	displayCurrent(index: number): void {
		const previous = document.querySelectorAll('.item-list .item.current');
		const current = document.querySelectorAll('.item-list .item')[index];
		previous.forEach(prev => {
			prev.classList.replace('border-dark', 'border-light');
			prev.classList.remove('current', 'font-weight-bold');
		});
		current.classList.add('border-dark', 'current', 'font-weight-bold');
		current.classList.remove('border-light');
	}

	displayFilters(filters: FilterValue[], bindTarget: EventTarget): void {
		const filterList = document.querySelector('.active-filter-list');
		filterList.innerHTML = filters
			.map(filter => `
				<a class="badge badge-primary filter"
					data-filter='${filter.id}'
					href="#"
					title="${filter.title}"
					onclick="return false;"
				>${filter.title.length > 20 + 3 ?
				filter.title.substring(0, 20) + '...' :
				filter.title} &times;
				</a>
			`)
			.concat(filters.length > 0 ?
				[`<a class="filter-clear" href="#" onclick="return false;">
					Clear
				</a>`] :
				[]
			)
			.join('');
		filterList.querySelectorAll<HTMLElement>('.filter').forEach(filter => {
			filter.addEventListener('click', e => {
				bindTarget.dispatchEvent(
					new RemoveFiltersEvent([filter.dataset.filter])
				);
			});
		});
		filterList.querySelectorAll('.filter-clear').forEach(clear => {
			clear.addEventListener('click', e => {
				bindTarget.dispatchEvent(new ClearFiltersEvent());
			});
		});
		document.querySelectorAll('.filter-list .filter')
			.forEach(div => {
				const input = div.querySelector('input');
				input.checked = false;
				div.classList.remove('border', 'bg-light', 'rounded');
			});
		filters.forEach(filter => {
			const input = document.querySelector<HTMLInputElement>(`
				.filter-list
				.filter
				input[data-filter='${JSON.stringify(filter)}']
			`);
			const div = input.closest('.filter');
			div.classList.add('border', 'bg-light', 'rounded');
			input.checked = true;
		});
	}

	displayFiltered(filtered: number[]): void {
		const items = Array.from(
			document.querySelectorAll('.item-list .item')
		);
		const active = items.filter((item, index) =>
			filtered.includes(index)
		);
		const inactive = items.filter((item, index) =>
			!filtered.includes(index)
		);
		active.forEach(item => {
			item.classList.remove('d-none');
		});
		inactive.forEach(item => {
			item.classList.add('d-none');
		});
	}

	displaySorted(order: number[]): void {
		const items: HTMLElement[] = Array.from(
			document.querySelectorAll('.item-list .item')
		);
		order.forEach((index, order) => {
			items[index].style.order = order.toString();
		});
	}
}

